import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import {
  ScrollText, Search, RefreshCw, ChevronLeft, ChevronRight, ShieldAlert, Download,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';

const PAGE_SIZE = 50;

function actionColor(action = '') {
  if (/delete|revoke|remove|disable/i.test(action)) return 'bg-red-500/15 text-red-400';
  if (/create|add|generate|enable/i.test(action)) return 'bg-emerald-500/15 text-emerald-400';
  if (/update|rotate|modify|set/i.test(action)) return 'bg-amber-500/15 text-amber-400';
  return 'bg-secondary text-muted-foreground';
}

function fmtTime(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function AuditLog() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = async (nextOffset = offset) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/audit-logs`, {
        params: { limit: PAGE_SIZE, offset: nextOffset },
        withCredentials: true,
      });
      setLogs(r.data?.logs || []);
      setTotal(r.data?.total || 0);
      setOffset(nextOffset);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Super-admin role required to view audit logs');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) load(0);
    // eslint-disable-next-line
  }, [isSuperAdmin]);

  const filtered = useMemo(() => {
    if (!q.trim()) return logs;
    const needle = q.toLowerCase();
    return logs.filter((l) =>
      [l.action, l.resource_type, l.resource_id, l.user_email, l.user_id, JSON.stringify(l.details)]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(needle)),
    );
  }, [logs, q]);

  const exportCsv = () => {
    const header = ['timestamp', 'actor', 'action', 'resource_type', 'resource_id', 'ip', 'details'];
    const rows = filtered.map((l) => [
      l.timestamp, l.user_email || l.user_id || 'system', l.action, l.resource_type || '',
      l.resource_id || '', l.ip_address || '', JSON.stringify(l.details || {}),
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cloudgenie-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto pt-16 px-6" data-testid="audit-log-denied">
        <Card className="p-8 text-center border-amber-500/30 bg-amber-500/5">
          <ShieldAlert className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold">Super-admin only</h2>
          <p className="text-sm text-muted-foreground mt-2">
            The platform audit trail is restricted to super-admins.
          </p>
        </Card>
      </div>
    );
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 space-y-5" data-testid="audit-log-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ScrollText className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">Audit log</h1>
            <p className="text-sm text-muted-foreground">
              Append-only record of every privileged action. {total.toLocaleString()} events.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="audit-export-csv">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => load(offset)} data-testid="audit-refresh">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter this page by action, resource, user…"
          className="pl-9"
          data-testid="audit-filter-input"
        />
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3 font-medium">Actor</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Resource</th>
                  <th className="px-4 py-3 font-medium">Details</th>
                  <th className="px-4 py-3 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-16 text-center text-muted-foreground" data-testid="audit-loading">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-16 text-center text-muted-foreground" data-testid="audit-empty">No audit events match.</td></tr>
                ) : (
                  filtered.map((l) => (
                    <tr key={l.id} className="border-b border-border/40 hover:bg-accent/40" data-testid="audit-row">
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtTime(l.timestamp)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{l.user_email || l.user_id || <span className="text-muted-foreground">system</span>}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge className={`${actionColor(l.action)} border-0 font-normal`}>{l.action}</Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-foreground">{l.resource_type || '—'}</span>
                        {l.resource_id && <span className="text-muted-foreground text-xs ml-1">#{String(l.resource_id).slice(0, 12)}</span>}
                      </td>
                      <td className="px-4 py-3 max-w-xs truncate text-muted-foreground" title={JSON.stringify(l.details || {})}>
                        {l.details && Object.keys(l.details).length ? JSON.stringify(l.details) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{l.ip_address || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground" data-testid="audit-page-info">Page {page} of {pages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0 || loading}
            onClick={() => load(Math.max(0, offset - PAGE_SIZE))} data-testid="audit-prev">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => load(offset + PAGE_SIZE)} data-testid="audit-next">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
